import {
  CmsAsset,
  ParentalRating,
  AssetGenre,
  LocalizedAssetInfo,
  SourceType,
  AssetType,
  EpgEvent,
} from "@nendaTypes/cms/CmsAssetType";
import { DateTime } from "@nendaTypes/cms/CmsCommonTypes";
import { ContentRights } from "@nendaTypes/cms/CmsContentRightsType";
import { LocalizedImage } from "@nendaTypes/cms/CmsImageType";
import { Media } from "@nendaTypes/cms/CmsMediaType";
import { CmsTag } from "@nendaTypes/cms/CmsTagType";
import { CmsAssetPlayability } from "@shared/cmsAssetPlayability";

export class CMSAsset implements CmsAsset {
  assetId: string;
  externalId: string;
  type: AssetType;
  sourceType: SourceType;
  provider: { providerId: number; name: string };
  originalTitle: string;
  localizedInfo?: LocalizedAssetInfo[];
  medias?: Media[];
  images?: LocalizedImage[];
  tags?: CmsTag[];
  parentalRatings?: ParentalRating[];
  contentRights?: ContentRights[];
  genres?: AssetGenre[];
  keywords?: string[];
  contributors?: any[];
  createdAt: DateTime;
  updatedAt: DateTime;
  epgEvents?: Array<EpgEvent>;
  playability?: CmsAssetPlayability;

  // VOD-specific fields
  seasonNumber?: number;
  episodeNumber?: number;
  seriesId?: string;
  seasonId?: string;
  countryOfOrigin?: string;
  copyrightInfo?: string;
  originalLanguage?: string;
  productionYear?: number;
  releaseYear?: number;
  studio?: string;
  eidrId?: string;
  duration?: number;

  constructor(asset: Partial<CmsAsset> = {}) {
    Object.assign(this, asset);
  }

  // Most likely not used for CMSAsset, but keeping for compatibility
  get isPublished(): boolean {
    return true;
  }

  get createdDate(): DateTime {
    return this.createdAt;
  }

  get changedDate(): DateTime {
    return this.updatedAt;
  }

  getUniqueId(): string {
    return this.assetId;
  }

  getType(): string {
    return this.type;
  }

  getDisplayName(): string {
    return this.getTitle() || this.getUniqueId();
  }

  getTitle(locale = "en") {
    if (!this.localizedInfo || this.localizedInfo.length === 0) {
      return this.originalTitle || "";
    }
    const localeItem =
      this.localizedInfo.find((l) => l.language === locale) ||
      this.localizedInfo[0];
    return localeItem.title || this.originalTitle || "";
  }

  getImage(locale = "en") {
    if (this.images && this.images.length > 0) {
      const localeImage =
        this.images.find((img) => img.language === locale) || this.images[0];
      return localeImage.url;
    }
    if (this.medias && this.medias.length > 0) {
      for (const media of this.medias) {
        if (media.mediaResources && media.mediaResources.length > 0) {
          const resource = media.mediaResources.find((r) => r.url);
          if (resource) return resource.url;
        }
      }
    }
    return "";
  }

  serialize() {
    return {
      ...this,
      images: this.images?.map((image) => ({
        ...image,
        url: image.blobPath ?? image.url,
      })),
      medias: this.medias?.map((media) => {
        if (
          typeof media === "object" &&
          media !== null &&
          ("_durationFromAlcom" in media || "_fromMpd" in media)
        ) {
          const { _durationFromAlcom, _fromMpd, ...rest } =
            media as unknown as {
              _durationFromAlcom?: boolean;
              _fromMpd?: boolean;
              [k: string]: unknown;
            };
          return rest;
        }
        return media;
      }),
    };
  }
}
